import { createModel } from "@rematch/core";
import { api } from "api";
import {
  ApiRolePermissionsUpdateUserPermissionRelationsPutRequest,
  GetUserPermissionRelationsQuery,
  PermissionVm,
  SimpleUserForUserRoleRelationVm,
  SimpleUserForUserRoleRelationVmPaginatedSearchResponse,
  UserPermissionRelationVm,
} from "api/generated/lumen";
import { RootModel } from "app/store";
import { mapToGetAllQuery } from "utils";
import {
  ListRequestParams,
  mergeListParams,
  mergeListParamsWithPagination,
  OrderByType,
} from "widgets/table/useTableUtils";

export type GetUserPermissionRelationsRequestParams =
  ListRequestParams<GetUserPermissionRelationsQuery>; // TODO

type UsersAndPermissionsAssignState = {
  relations?: UserPermissionRelationVm[] | null;
  listParams: GetUserPermissionRelationsRequestParams;
  permissions?: PermissionVm[] | null;
  users: SimpleUserForUserRoleRelationVm[];
  filterPermission?: string | null;
  isSaveDisabled: boolean;
};

const initialState: UsersAndPermissionsAssignState = {
  relations: [],
  listParams: {
    pageSize: 10,
    order: [
      {
        orderBy: "name",
        orderByType: OrderByType.Ascending,
      },
    ],
  },
  permissions: [],
  users: [],
  filterPermission: null,
  isSaveDisabled: true,
};

export const usersAndPermissionsAssign = createModel<RootModel>()({
  state: initialState,
  reducers: {
    resetListParams(state) {
      state.listParams = {
        ...initialState.listParams,
        order: initialState.listParams.order,
      };
      state.filterPermission = "";
    },
    setUserPermissionRelations(
      state,
      payload: UserPermissionRelationVm[] | null | undefined
    ) {
      state.relations = payload;
    },
    setPermissions(state, payload: PermissionVm[] | undefined | null) {
      state.permissions = payload;
    },
    setFilterPermission(state, payload: string | null) {
      state.filterPermission = payload;
    },
    setUserList(
      state,
      {
        data,
        listParams,
      }: {
        data: SimpleUserForUserRoleRelationVm[];
        listParams: GetUserPermissionRelationsRequestParams;
      }
    ) {
      state.users = data;
      state.listParams = listParams;
    },
    setIsSaveDisabled(state, payload: boolean) {
      state.isSaveDisabled = payload;
    },
    reset: () => initialState,
  },

  effects: (dispatch) => {
    const { usersAndPermissionsAssign } = dispatch;

    return {
      async getUserPermissionRelations(
        params: GetUserPermissionRelationsRequestParams = {},
        state
      ): Promise<void> {
        const mergedParams = mergeListParams(
          state.usersAndPermissionsAssign.listParams,
          {
            ...params,
            filter: {
              ...params.filter,
              permission: state.usersAndPermissionsAssign.filterPermission,
            },
            order: [
              {
                orderBy: "name",
                orderByType: OrderByType.Ascending,
              },
            ],
          }
        );

        const { userIds, permission, ...filters } = mergedParams.filter ?? {};

        const getAllQuery = mapToGetAllQuery({
          ...mergedParams,
          filter: filters,
        });
        const data =
          await api.lumen.rolePermissions.apiRolePermissionsGetUserPermissionRelationsPost(
            {
              getUserPermissionRelationsQuery: {
                ...getAllQuery,
                permission,
                userIds,
              },
            }
          );

        const { users, permissions, relations } = data;

        dispatch(
          usersAndPermissionsAssign.setUserPermissionRelations(relations)
        );
        dispatch(usersAndPermissionsAssign.setPermissions(permissions));

        const { items, ...pagination } =
          users as SimpleUserForUserRoleRelationVmPaginatedSearchResponse;

        dispatch(
          usersAndPermissionsAssign.setUserList({
            data: items || [],
            listParams: mergeListParamsWithPagination(mergedParams, pagination),
          })
        );
      },

      async updateUserPermissionRelations(
        updateUserPermissionRelationsCommand: ApiRolePermissionsUpdateUserPermissionRelationsPutRequest
      ) {
        await api.lumen.rolePermissions.apiRolePermissionsUpdateUserPermissionRelationsPut(
          updateUserPermissionRelationsCommand
        );
      },
    };
  },
});
